import { Box, Button, Link, Stack, Typography } from '@dtx-company/design-system/src'
import { DashboardDisplay } from './DashboardDisplay'
import { FC, ReactElement, useMemo, useState } from 'react'
import { IconComponent } from '@dtx-company/design-system/src/types/util'
import { NavLinksProps } from './NavLinks.types'
import { Shadows } from '@mui/material/styles'
import { SignInOptions } from './SignInOptions'
import { TOP_NAV_DESKTOP_LINKS, TopNavDocumentData, TopNavSlice, topNavData } from './topNavData'
import { Tooltip } from '@mui/material'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useScreenSizeBreakpoints } from '@dtx-company/app/shared/common/src/hooks/useScreenSizeBreakpoints'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Image from 'next/image'

const getIcon = (
  navItem: TopNavSlice,
  activeNav: TopNavSlice | null
): IconComponent | undefined => {
  if (navItem.items?.length > 0)
    return activeNav?.primary.header === navItem.primary.header
      ? ExpandMoreIcon
      : KeyboardArrowRightIcon
  return undefined
}

export const MarketingNavLinks: FC<NavLinksProps> = ({ isLoggedIn, isFPHomepage = false }) => {
  const { isMobile } = useScreenSizeBreakpoints()
  const [activeNav, setActiveNav] = useState<TopNavSlice | null>(null)

  return (
    <>
      <Stack
        onMouseLeave={() => {
          setActiveNav(null)
        }}
        direction={'row'}
        data-testid="marketing-nav-desktop"
        sx={{
          justifyContent: 'end',
          alignItems: 'center',
          gap: 8,
          width: '100%'
        }}
      >
        <NavigationMenuTooltip activeNav={activeNav} prismic={topNavData}>
          <Stack direction={'row'}>
            {TOP_NAV_DESKTOP_LINKS.map(navItem => (
              <Box
                key={navItem.primary.header}
                onMouseEnter={() => {
                  fireAnalyticsEvent('Global_MarketingNav_Hovered_NavItem', {
                    value: navItem.primary.header
                  })
                  setActiveNav(navItem)
                }}
                onClick={() => {
                  fireAnalyticsEvent('Global_MarketingNav_Clicked_NavItem', {
                    value: navItem.primary.header
                  })
                  setActiveNav(navItem)
                }}
              >
                <Button
                  variant={'text'}
                  href={navItem.primary.header_link}
                  endIcon={getIcon(navItem, activeNav)}
                >
                  {navItem.primary.header}
                </Button>
              </Box>
            ))}
          </Stack>
        </NavigationMenuTooltip>

        {/* Auth/Dashboard buttons */}
        {isLoggedIn && !isMobile && <DashboardDisplay />}
        {!isLoggedIn && <SignInOptions isFPHomepage={isFPHomepage} />}
      </Stack>
    </>
  )
}

export const NavigationMenuTooltip: FC<{
  activeNav?: TopNavSlice | null
  prismic: TopNavDocumentData
  children: ReactElement
}> = ({ activeNav, prismic, children }) => {
  return (
    <Tooltip
      arrow={false}
      open={Boolean(activeNav)}
      componentsProps={{
        popper: {
          sx: {
            width: '100vw'
          }
        },
        tooltip: {
          sx: {
            backgroundColor: 'white',
            color: 'black',
            width: '100%',
            maxWidth: '100%',
            boxShadow: theme => (theme.shadows as Shadows)[1],
            p: 0,
            borderRadius: theme => theme.spacing(6)
          }
        }
      }}
      title={
        activeNav?.items &&
        activeNav?.items.length > 0 && (
          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: '3fr 2fr',
              p: 6
            }}
          >
            <Box sx={{ p: 6 }}>
              <NavigationMenu navItem={activeNav} />
            </Box>
            <Stack
              gap={6}
              direction={'row'}
              sx={theme => ({
                p: 6,
                backgroundColor: theme.palette.surface['1'],
                borderRadius: theme => theme.spacing(6)
              })}
            >
              <Image
                style={{ borderRadius: '20px', backgroundColor: 'white' }}
                width={178}
                height={157}
                src={prismic.data.contact_us_image}
                alt={'FlowID'}
                loading={'lazy'}
              />
              <Stack gap={6}>
                <Typography
                  variant={'subtitleMedium'}
                  sx={{ color: theme => theme.palette.tertiary.main }}
                >
                  {prismic.data.contact_us_title}
                </Typography>
                <Typography variant={'bodySmall'} color={'primary.light'}>
                  {prismic.data.contactus_description}
                </Typography>
                <Box
                  sx={{
                    a: {
                      textDecoration: 'underline',
                      display: 'flex',
                      alignItems: 'center',
                      color: 'primary.main'
                    }
                  }}
                >
                  <Link href={prismic.data.contact_us_link}>
                    <Typography variant={'labelLarge'}>
                      {prismic.data.contact_us_link_text}
                    </Typography>
                  </Link>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        )
      }
    >
      {children}
    </Tooltip>
  )
}

const NavItem: FC<{
  item: { link_title: string; dropdown_link: string; subsection?: string }
  onClick: () => Promise<void[]>
}> = props => {
  return (
    <Stack gap={2}>
      <Stack
        direction={'row'}
        gap={2}
        alignItems={'center'}
        sx={theme => ({
          a: {
            color: theme.palette.primary.main,
            borderBottom: `1px solid transparent`,
            textDecoration: 'none',
            textDecorationColor: 'transparent'
          },
          'a:hover': {
            backgroundColor: 'transparent',
            borderBottom: `1px solid ${theme.palette.tertiary.main}`,
            color: theme.palette.tertiary.main,
            textDecoration: 'none',
            textDecorationColor: 'transparent'
          }
        })}
      >
        <Link href={props.item.dropdown_link} onClick={props.onClick}>
          <Typography variant={'bodyMedium'}>{props.item.link_title}</Typography>
        </Link>
      </Stack>
    </Stack>
  )
}

export const NavigationMenu: FC<{
  navItem?: TopNavSlice | null
}> = ({ navItem }) => {
  const subsections: Record<string, TopNavSlice['items']> = useMemo(
    () =>
      ((navItem?.items || []) as TopNavSlice['items']).reduce((acc: any, item) => {
        const key = item.subsection ?? ''
        if (!acc[key]) {
          acc[key] = [item]
        } else {
          acc[key].push(item)
        }
        return acc
      }, {}),
    [navItem]
  )

  return (
    <Stack
      sx={{
        display: 'grid',
        gap: 4,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
      }}
    >
      {Object.keys(subsections || {}).map(subsection => (
        <>
          {subsection ? (
            <Stack key={`subsection-${subsection}`} gap={4}>
              <Typography
                variant={'subtitleMedium'}
                sx={{ color: theme => theme.palette.tertiary.main }}
              >
                {subsection}
              </Typography>

              <Stack
                gap={4}
                sx={{
                  display: 'flex',
                  pl: { xs: 4, lg: 0 }
                }}
              >
                {subsections[subsection]?.map((item, index) => (
                  <NavItem
                    key={`${subsection}-${index}`}
                    item={item}
                    onClick={() =>
                      fireAnalyticsEvent('Global_MarketingNav_Clicked_NavItem', {
                        value: item.link_title
                      })
                    }
                  />
                ))}
              </Stack>
            </Stack>
          ) : (
            <>
              {subsections[subsection]?.map((item, index) => (
                <NavItem
                  key={`${subsection}-${index}`}
                  item={item}
                  onClick={() =>
                    fireAnalyticsEvent('Global_MarketingNav_Clicked_NavItem', {
                      value: item.link_title
                    })
                  }
                />
              ))}
            </>
          )}
        </>
      ))}
    </Stack>
  )
}
